import { ILoadPlace } from '@/features/google-places/google-places-hooks'
import {
    GoogleAddressTag,
    IGoogleAddress,
    IGoogleAddressOption,
} from '@/features/google-places/google-places-restaurants-search'
import { IGooglePlacesSelectOption } from '@/features/google-places/google-places-types'
import { Locale } from '@/setup/locales'
import { run } from '@/utils/run'
import { Extend } from '@/utils/typings/extend'

export function createLoadAddressHandler(
    locale: Locale,
    loadPlace: ILoadPlace,
    onLoad: (addressOption: IGoogleAddressOption | null, address: IGoogleAddress | null) => void
) {
    return (newSelectedOption: IGooglePlacesSelectOption | null) => {
        const rawAddress = run((): IGoogleAddress | null | Promise<IGoogleAddress | null> => {
            if (!newSelectedOption) return null

            // Then the selected option is a location option and the value is an address
            if (!('place_id' in newSelectedOption.value)) return newSelectedOption.value

            const placeId = newSelectedOption.value.place_id

            return loadFullPlaceWithAddress(loadPlace, placeId, locale)
        })

        if (rawAddress instanceof Promise) {
            rawAddress.then((address) => {
                onLoad(parseAddressOption(address), address)
            })

            return
        }

        onLoad(parseAddressOption(rawAddress), rawAddress)
    }
}

export function loadFullPlaceWithAddress(loadPlace: ILoadPlace, placeId: string, locale: Locale) {
    return loadPlace(placeId, locale).then((place): IGoogleAddress | null => {
        if (place) {
            const partialAddress = place.address_components?.reduce<Partial<IGoogleAddress>>(
                (acc, { types, long_name }) => {
                    const field = run((): keyof IGoogleAddress | null => {
                        if (types.includes('country')) return 'country'
                        if (types.includes('postal_code')) return 'zip'
                        if (types.includes('route')) return 'street'
                        if (types.includes('street_number')) return 'streetNumber'

                        if (['locality', 'postal_town'].some((type) => types.includes(type))) {
                            return 'city'
                        }

                        return null
                    })

                    if (!field) return acc

                    return {
                        ...acc,
                        [field]: long_name,
                    }
                },
                {}
            )

            const address: IGoogleAddress | Partial<IGoogleAddress> = {
                ...partialAddress,
                placeId,
                lat: place.geometry?.location?.lat(),
                lng: place.geometry?.location?.lng(),
                name: place.name,
                photos: place.photos,
                priceLevel: place.price_level,
                tags: place.types?.reduce<GoogleAddressTag[]>((acc, type) => {
                    switch (type) {
                        case 'cafe':
                            return [...acc, GoogleAddressTag.CAFE]

                        case 'restaurant':
                            return [...acc, GoogleAddressTag.RESTAURANT]

                        default:
                            return acc
                    }
                }, []),
            }

            const isValid = assertIsAddress(address)

            return isValid ? address : null
        }

        return null
    })
}

export const assertIsAddress = (
    rawAddress: IGoogleAddress | undefined | Partial<IGoogleAddress>
): rawAddress is IGoogleAddress => {
    if (!rawAddress) {
        return false
    }

    const address = rawAddress as IGoogleAddress

    const isSufficientCountry = Boolean(address.country && typeof address.country === 'string')
    const isSufficientZip = Boolean(address.zip && typeof address.zip === 'string')
    const isSufficientCity = Boolean(address.city && typeof address.city === 'string')
    const isSufficientStreet = Boolean(address.street && typeof address.street === 'string')
    const isSufficientStreetNumber = Boolean(
        address.streetNumber && typeof address.streetNumber === 'string'
    )

    const isSufficientBaseAddress =
        isSufficientCountry &&
        isSufficientCity &&
        isSufficientStreet &&
        typeof rawAddress.lat === 'number' &&
        typeof rawAddress.lng === 'number'

    // If the street number is not provided, the street does often not offer a ZIP code aswell
    return isSufficientBaseAddress && isSufficientZip && isSufficientStreetNumber
}

export function parseReadableAddress(
    address: Extend<IGoogleAddress>,
    type:
        | 'street'
        | 'city'
        | 'country'
        | 'street-city'
        | 'city-street'
        | 'city-country'
        | 'street-city-country'
): string {
    const streetStr =
        address.street && address.streetNumber
            ? `${address.street} ${address.streetNumber}`
            : address.street

    const cityStr = address.zip && address.city ? `${address.zip} ${address.city}` : address.city

    const countryStr = address.country

    switch (type) {
        case 'street':
            return streetStr

        case 'city':
            return cityStr

        case 'country':
            return countryStr

        case 'street-city':
            return `${streetStr}, ${cityStr}`

        case 'city-street':
            return `${cityStr}, ${streetStr}`

        case 'city-country':
            return `${cityStr}, ${countryStr}`

        case 'street-city-country':
            return `${streetStr}, ${cityStr}, ${countryStr}`
    }
}

export function parseAddressOption(
    address: IGoogleAddress | null | undefined
): IGoogleAddressOption | null {
    if (!address) return null

    return {
        label: parseFormattedAddress(address),
        value: address,
    }
}

export function parseFormattedAddress(address: IGoogleAddress): string {
    return `${[
        address.street && address.streetNumber
            ? `${address.street} ${address.streetNumber}`
            : undefined,
        address.zip,
        address.city,
        address.country,
    ]
        .filter(Boolean)
        .join(', ')}`
}
